import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ArrowUpDown } from "lucide-react";

export const Route = createFileRoute("/_authenticated/reports/heatmap")({
  component: HeatmapReport,
});

interface HeatmapRow {
  projectId: string;
  projectName: string;
  flhaCompletionPct: number;
  flhaSubmittedToday: number;
  flhaExpectedToday: number;
  openCorrectiveActions: number;
  overdueCorrectiveActions: number;
  expiringCerts: number;
  lastIncidentDays: number | null;
  score: number;
}

type SortKey =
  | "score"
  | "projectName"
  | "flhaCompletionPct"
  | "openCorrectiveActions"
  | "expiringCerts"
  | "lastIncidentDays";

// Color ramp helpers
function scoreColor(score: number): string {
  if (score >= 85) return "bg-emerald-100 text-emerald-900 border-emerald-200";
  if (score >= 60) return "bg-amber-100 text-amber-900 border-amber-200";
  return "bg-red-100 text-red-900 border-red-200";
}

function pctColor(pct: number): string {
  if (pct >= 85) return "bg-emerald-50 text-emerald-800";
  if (pct >= 50) return "bg-amber-50 text-amber-800";
  return "bg-red-50 text-red-800";
}

function countColor(n: number, thresholdWarn = 1, thresholdBad = 3): string {
  if (n === 0) return "bg-emerald-50 text-emerald-800";
  if (n < thresholdBad && n >= thresholdWarn) return "bg-amber-50 text-amber-800";
  return "bg-red-50 text-red-800";
}

function incidentDaysColor(days: number | null): string {
  if (days === null) return "bg-slate-50 text-slate-600";
  if (days >= 90) return "bg-emerald-50 text-emerald-800";
  if (days >= 30) return "bg-amber-50 text-amber-800";
  return "bg-red-50 text-red-800";
}

function HeatmapReport() {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "heatmap"],
    queryFn: () => apiGet<{ data: HeatmapRow[] }>("/api/reports/heatmap"),
  });

  const rows = data?.data ?? [];

  const sortedRows = [...rows].sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case "projectName":
        cmp = a.projectName.localeCompare(b.projectName);
        break;
      case "flhaCompletionPct":
        cmp = a.flhaCompletionPct - b.flhaCompletionPct;
        break;
      case "openCorrectiveActions":
        cmp = a.openCorrectiveActions - b.openCorrectiveActions;
        break;
      case "expiringCerts":
        cmp = a.expiringCerts - b.expiringCerts;
        break;
      case "lastIncidentDays": {
        const da = a.lastIncidentDays ?? Number.MAX_SAFE_INTEGER;
        const db = b.lastIncidentDays ?? Number.MAX_SAFE_INTEGER;
        cmp = da - db;
        break;
      }
      case "score":
      default:
        cmp = a.score - b.score;
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function handleRowClick(projectId: string) {
    navigate({ to: "/projects/$projectId", params: { projectId } });
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-lg bg-muted/40"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-destructive text-center py-8">
        Failed to load heat map. Please refresh and try again.
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No active projects found.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Compliance heat map — projects ranked by overall score (worst first).
        Click a row to view project details.
      </p>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th
                onClick={() => toggleSort("projectName")}
                className="sticky left-0 z-10 cursor-pointer bg-muted/60 px-3 py-2 backdrop-blur-sm"
              >
                <span className="inline-flex items-center gap-1">
                  Project
                  <ArrowUpDown className="h-3 w-3 opacity-60" />
                </span>
              </th>
              <th
                onClick={() => toggleSort("flhaCompletionPct")}
                className="cursor-pointer px-3 py-2 whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">
                  FLHA Today
                  <ArrowUpDown className="h-3 w-3 opacity-60" />
                </span>
              </th>
              <th
                onClick={() => toggleSort("openCorrectiveActions")}
                className="cursor-pointer px-3 py-2 whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">
                  Open Actions
                  <ArrowUpDown className="h-3 w-3 opacity-60" />
                </span>
              </th>
              <th
                onClick={() => toggleSort("expiringCerts")}
                className="cursor-pointer px-3 py-2 whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">
                  Certs (60d)
                  <ArrowUpDown className="h-3 w-3 opacity-60" />
                </span>
              </th>
              <th
                onClick={() => toggleSort("lastIncidentDays")}
                className="cursor-pointer px-3 py-2 whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">
                  Last Incident
                  <ArrowUpDown className="h-3 w-3 opacity-60" />
                </span>
              </th>
              <th
                onClick={() => toggleSort("score")}
                className="cursor-pointer px-3 py-2 whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">
                  Score
                  <ArrowUpDown className="h-3 w-3 opacity-60" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr
                key={row.projectId}
                onClick={() => handleRowClick(row.projectId)}
                className="cursor-pointer border-t border-border/60 hover:bg-muted/30"
              >
                <td className="sticky left-0 z-10 bg-card px-3 py-2 font-medium text-text-primary whitespace-nowrap">
                  {row.projectName}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold",
                      pctColor(row.flhaCompletionPct)
                    )}
                  >
                    {row.flhaSubmittedToday}/{row.flhaExpectedToday} (
                    {row.flhaCompletionPct}%)
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold",
                      countColor(row.openCorrectiveActions)
                    )}
                  >
                    {row.openCorrectiveActions}
                    {row.overdueCorrectiveActions > 0 && (
                      <span className="ml-1 opacity-75">
                        ({row.overdueCorrectiveActions} overdue)
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold",
                      countColor(row.expiringCerts)
                    )}
                  >
                    {row.expiringCerts}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold",
                      incidentDaysColor(row.lastIncidentDays)
                    )}
                  >
                    {row.lastIncidentDays === null
                      ? "None"
                      : `${row.lastIncidentDays}d ago`}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold",
                      scoreColor(row.score)
                    )}
                  >
                    {row.score}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="font-semibold">Score:</span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border border-emerald-200 bg-emerald-100" />
          85+ healthy
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border border-amber-200 bg-amber-100" />
          60-84 watch
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border border-red-200 bg-red-100" />
          &lt;60 action required
        </span>
      </div>
    </div>
  );
}
